import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, PackageSearch, CheckCircle2, CreditCard } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";

const STEPS = ["requested", "quoted", "accepted", "picked_up", "sailed", "arrived"];
const LABEL: Record<string, string> = { requested: "Request received", quoted: "Quote sent", accepted: "Quote accepted", picked_up: "Picked up", sailed: "Sailed", arrived: "Arrived in Liberia", declined: "Declined", cancelled: "Cancelled" };

const Steps = ({ status }: { status: string }) => {
  if (!STEPS.includes(status)) return <p className="rounded-xl bg-slate-100 p-3 text-sm font-semibold text-slate-600" data-testid="track-status">{LABEL[status]}</p>;
  const idx = STEPS.indexOf(status);
  return (
    <ol className="grid grid-cols-3 gap-2 sm:grid-cols-6" data-testid="track-status" data-status={status}>
      {STEPS.map((s, i) => <li key={s} className={`rounded-xl p-3 text-center text-xs font-semibold ${i <= idx ? "bg-navy text-white" : "bg-navy/5 text-navy/40"}`}>{LABEL[s]}</li>)}
    </ol>
  );
};

const Pay = ({ r, creds }: { r: any; creds: () => any }) => {
  const [busy, setBusy] = useState(false);
  if (r.payment_status === "paid") return <p className="mt-4 flex items-center gap-2 text-sm font-semibold text-emerald-700" data-testid="track-paid"><CheckCircle2 size={16} /> Paid {r.paid_at ? fmtDate(r.paid_at) : ""}. Thank you.</p>;
  if (!["accepted", "picked_up", "sailed", "arrived"].includes(r.status)) return null;
  const pay = async () => {
    setBusy(true);
    try { window.location.href = (await api.post("/payments/shipment-checkout", { ...creds(), origin_url: window.location.origin })).data.checkout_url; }
    catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  return (
    <div className="mt-4 space-y-2">
      <button disabled={busy} onClick={pay} className="btn-navy" data-testid="track-pay">{busy ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />} Pay securely by card</button>
      <p className="text-xs text-navy/50">Secure card checkout by Stripe. We never see or store your card details.</p>
    </div>
  );
};

const Updates = ({ u }: { u: Record<string, any> }) => {
  const items = ["picked_up", "sailed", "arrived"].filter((k) => u[k]);
  if (!items.length) return null;
  return <ul className="space-y-1 text-sm" data-testid="track-updates">{items.map((k) => <li key={k}><b>{LABEL[k]}</b> · {fmtDate(u[k].at)}{u[k].note ? ` · ${u[k].note}` : ""}</li>)}</ul>;
};

const Pickup = ({ r, creds, onDone }: { r: any; creds: () => any; onDone: (x: any) => void }) => {
  const [data, setData] = useState<any>(null);
  const [pick, setPick] = useState({ date: "", slot: "" });
  const [busy, setBusy] = useState(false);
  const canEdit = r.payment_status === "paid" && r.status === "accepted";
  useEffect(() => {
    if (canEdit) { api.post("/shipping/track/pickup-slots", creds()).then((x) => setData(x.data)).catch(() => {}); }
  }, [canEdit, r.pickup?.scheduled_at]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setBusy(true);
    try { onDone((await api.post("/shipping/track/pickup", { ...creds(), ...pick })).data); toast.success(r.pickup ? "Pickup updated" : "Pickup booked"); setPick({ date: "", slot: "" }); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  if (r.payment_status !== "paid") return null;
  const day = data?.days.find((d: any) => d.date === pick.date);
  return (
    <div className="rounded-2xl border border-navy/10 p-5" data-testid="track-pickup">
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-brass-deep">Pickup</p>
      {r.pickup ? <p className="mt-1 font-semibold" data-testid="track-pickup-current">{fmtDate(r.pickup.date)} · {r.pickup.label}</p> : <p className="mt-1 text-sm text-navy/65">Choose when we should collect your items.</p>}
      {canEdit && data && (
        <div className="mt-4 space-y-3">
          <p className="text-[11px] text-navy/45 sm:hidden">Swipe for more dates →</p>
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="track-pickup-days">
            {data.days.map((d: any) => {
              const full = d.slots.every((s: any) => !s.available);
              const dt = new Date(d.date + "T12:00:00");
              return <button key={d.date} type="button" disabled={full} onClick={() => setPick({ date: d.date, slot: "" })} data-testid={`pickup-day-${d.date}`}
                className={`shrink-0 rounded-xl border px-3 py-2 text-center text-xs transition-colors ${pick.date === d.date ? "border-navy bg-navy text-white" : r.pickup?.date === d.date ? "border-brass bg-brass-soft" : "border-navy/15 hover:border-navy/40"} disabled:opacity-30`}>
                <span className="block font-semibold">{dt.toLocaleDateString(undefined, { weekday: "short" })}</span>{dt.toLocaleDateString(undefined, { day: "numeric", month: "short" })}</button>;
            })}
          </div>
          {day && <div className="grid gap-2 sm:grid-cols-3">{day.slots.map((s: any) => (
            <button key={s.id} type="button" disabled={!s.available} onClick={() => setPick({ ...pick, slot: s.id })} data-testid={`pickup-slot-${s.id}`}
              className={`rounded-xl border p-3 text-sm transition-colors ${pick.slot === s.id ? "border-navy bg-navy text-white" : r.pickup?.date === pick.date && r.pickup?.slot === s.id ? "border-brass bg-brass-soft" : "border-navy/15 hover:border-navy/40"} disabled:opacity-30`}>{s.label}{r.pickup?.date === pick.date && r.pickup?.slot === s.id ? " · current" : ""}{!s.available && " · full"}</button>))}</div>}
          <button disabled={busy || !pick.date || !pick.slot} onClick={save} className="btn-gold" data-testid="track-pickup-save">{busy && <Loader2 size={16} className="animate-spin" />} {r.pickup ? "Change pickup" : "Book pickup"}</button>
          <p className="text-xs text-navy/50">Pickup in {r.pickup_city}, {r.pickup_state}. Our team calls before arriving to confirm the address. You can change the slot until pickup.</p>
        </div>
      )}
    </div>
  );
};

const Quote = ({ r, onAccept, busy, creds }: { r: any; onAccept: () => void; busy: boolean; creds: () => any }) => {
  const q = r.quote;
  if (!q) return <p className="text-sm text-navy/60">Our team is reviewing your request and will email you a quote.</p>;
  return (
    <div className="rounded-2xl border border-brass/50 bg-brass-soft p-5" data-testid="track-quote">
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-brass-deep">Your quote</p>
      <p className="mt-1 text-3xl font-bold" data-testid="track-quote-amount">{q.currency} {Number(q.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
      <p className="mt-2 whitespace-pre-line text-sm text-navy/75">{q.details}</p>
      <p className="mt-2 text-xs text-navy/55">Valid until {q.valid_until}{q.expired ? " (expired)" : ""}</p>
      {r.status === "quoted" && !q.expired && <button disabled={busy} onClick={onAccept} className="btn-gold mt-4" data-testid="track-accept">{busy && <Loader2 size={16} className="animate-spin" />} Accept this quote</button>}
      {r.status !== "quoted" && r.status !== "requested" && r.status !== "declined" && r.status !== "cancelled" && r.payment_status !== "paid" && <p className="mt-4 flex items-center gap-2 text-sm font-semibold text-emerald-700" data-testid="track-accepted"><CheckCircle2 size={16} /> Quote accepted.</p>}
      <Pay r={r} creds={creds} />
      <p className="mt-3 text-xs text-navy/50">You only pay after accepting your quote. Our team confirms pickup with you directly.</p>
    </div>
  );
};

export default function ShipmentTrack() {
  const [sp] = useSearchParams();
  const [f, setF] = useState({ reference: sp.get("ref") || "", email: "" });
  const token = sp.get("t") || "";
  const [r, setR] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const creds = () => ({ reference: f.reference, ...(f.email ? { email: f.email } : { token }) });
  const lookup = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    setR(null);
    try { setR((await api.post("/shipping/track", creds())).data); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  useEffect(() => {
    if (token && f.reference) lookup();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const accept = async () => {
    if (!window.confirm("Accept this quote? You can then pay securely by card, and our team will contact you to arrange pickup.")) return;
    setBusy(true);
    try { setR((await api.post("/shipping/track/accept", creds())).data); toast.success("Quote accepted"); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="container-x max-w-3xl pb-28 pt-16" data-testid="track-page">
      <p className="eyebrow">Shipping</p>
      <h1 className="mt-3 text-4xl tracking-tight sm:text-5xl">Track my shipment request</h1>
      <form onSubmit={lookup} className="card-lux mt-8 grid gap-3 p-6 sm:grid-cols-[1fr_1fr_auto] sm:items-end" data-testid="track-form">
        <label><span className="label">Reference</span><input required value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="TL-SR-XXXXXXX" className="field font-mono uppercase" data-testid="track-reference" /></label>
        <label><span className="label">Email used on the request</span><input type="email" required={!token} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="field" data-testid="track-email" /></label>
        <button disabled={busy} className="btn-gold" data-testid="track-submit">{busy ? <Loader2 size={16} className="animate-spin" /> : <PackageSearch size={16} />} Find</button>
      </form>
      {r && (
        <div className="card-lux mt-6 space-y-5 p-6" data-testid="track-result">
          <div><p className="font-mono text-sm font-bold">{r.reference}</p>
            <p className="text-sm text-navy/65">{r.quantity} × {r.cargo_type} ({r.size}) · {r.pickup_city}, {r.pickup_state} → {r.destination_city}, Liberia · requested {fmtDate(r.created_at)}</p></div>
          <Steps status={r.status} />
          <Updates u={r.updates || {}} />
          <Quote r={r} onAccept={accept} busy={busy} creds={creds} />
          <Pickup r={r} creds={creds} onDone={setR} />
        </div>
      )}
      <p className="mt-6 text-sm text-navy/55">Need help? <Link to="/support" className="underline">Contact support</Link>.</p>
    </div>
  );
}
